import React, { useEffect, useRef, useState } from 'react';
import { Crop, ImagePlus, Upload } from 'lucide-react';
import { KYC_ACCEPT, validateKycFile } from './kycComplianceData';
import KycImageCropModal, { ID_CARD_ASPECT } from './KycImageCropModal';
import { isCroppableImageFile } from './kycCropUtils';

/**
 * KYC document image picker with optional crop (for CNIC / ID photos).
 * PDFs skip crop and are used as-is.
 */
export default function KycImageUploadField({
  label,
  hint,
  value,
  onChange,
  disabled = false,
  accept = KYC_ACCEPT,
  cropTitle = 'Crop document',
  aspect = ID_CARD_ASPECT,
  onError,
}) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [cropSrc, setCropSrc] = useState('');
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingName, setPendingName] = useState('kyc-cropped.jpg');

  useEffect(() => {
    if (!value || !isCroppableImageFile(value)) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  useEffect(
    () => () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    },
    [cropSrc]
  );

  const openCropForFile = (file) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    const url = URL.createObjectURL(file);
    const base = String(file.name || 'document').replace(/\.[^.]+$/, '');
    setPendingName(`${base}-cropped.jpg`);
    setCropSrc(url);
    setCropOpen(true);
  };

  const handlePick = (e) => {
    const input = e.target;
    const file = input.files?.[0] ?? null;
    if (!file) {
      onChange?.(null);
      return;
    }
    const err = validateKycFile(file);
    if (err) {
      onError?.(err);
      input.value = '';
      onChange?.(null);
      return;
    }
    onError?.('');

    if (isCroppableImageFile(file)) {
      openCropForFile(file);
    } else {
      onChange?.(file);
    }
    input.value = '';
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc('');
    }
  };

  const handleCropApply = (croppedFile) => {
    onChange?.(croppedFile);
    setCropOpen(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc('');
    }
  };

  return (
    <div className="kyc-image-upload-field">
      <label className="kyc-label">{label}</label>
      {hint ? <p className="kyc-hint">{hint}</p> : null}

      <div className="kyc-file-picker">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handlePick}
          className="kyc-file-input"
          disabled={disabled}
        />
        <button
          type="button"
          className="kyc-file-picker__btn"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={15} strokeWidth={2.25} aria-hidden />
          Choose file
        </button>
        {!value ? <span className="kyc-file-picker__empty">No file chosen</span> : null}
      </div>

      {value ? (
        <div className="kyc-image-upload-preview">
          {previewUrl ? (
            <img src={previewUrl} alt={`${label} preview`} className="kyc-image-upload-thumb" />
          ) : (
            <div className="kyc-image-upload-pdf">
              <ImagePlus size={18} aria-hidden />
              <span>PDF selected</span>
            </div>
          )}
          <div className="kyc-image-upload-meta">
            <span className="kyc-file-name">{value.name}</span>
            {isCroppableImageFile(value) ? (
              <button
                type="button"
                className="kyc-btn kyc-btn--ghost kyc-btn--sm"
                onClick={() => openCropForFile(value)}
                disabled={disabled}
              >
                <Crop size={14} strokeWidth={2} aria-hidden />
                Crop / adjust
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <KycImageCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        title={cropTitle}
        aspect={aspect}
        fileName={pendingName}
        onCancel={handleCropCancel}
        onApply={handleCropApply}
      />
    </div>
  );
}
